import React, { useState, useEffect } from 'react';
import { Container, Card, Table, Button, Badge, Alert, Row, Col, Form, Nav, Spinner } from 'react-bootstrap';
import { 
    FaCheck, FaSync, FaEnvelope, FaCheckCircle, 
    FaDollarSign, FaUserTie, FaClipboardList, 
    FaShieldAlt, FaFileContract, FaSave, FaTrash 
} from 'react-icons/fa'; 
import api from '../services/api';

const AdminDashboard = () => {
    const [activeTab, setActiveTab] = useState('orders'); 
    const [loading, setLoading] = useState(true);
    const [message, setMessage] = useState(null);
    const [orders, setOrders] = useState([]);
    const [agencies, setAgencies] = useState([]);
    const [agencyForm, setAgencyForm] = useState({ email: '', agencyName: '', agencySlug: '' });
    const [hubData, setHubData] = useState({});
    const [syncingId, setSyncingId] = useState(null);
    const [processingId, setProcessingId] = useState(null);

    const [tosContent, setTosContent] = useState('');
    const [savingTos, setSavingTos] = useState(false);

const fetchData = async () => {
    setLoading(true);
    setMessage(null);
    try {
        const ts = Date.now();
        // SỬA: Thêm tiền tố /api/ vào tất cả các route admin
        const [ordersRes, agenciesRes, tosRes] = await Promise.all([
            api.get(`/api/order/admin/all-orders?t=${ts}`), // Đã sửa đường dẫn
            api.get(`/api/admin/agencies?t=${ts}`).catch(() => ({ data: { agencies: [] } })),
            api.get(`/api/admin/get-tos?t=${ts}`).catch(() => ({ data: { content: '' } }))
        ]);

        console.log("✅ Dữ liệu Admin nhận được:", ordersRes.data.orders);
        setOrders(ordersRes.data.orders || []);
        setAgencies(agenciesRes.data.agencies || []);
        setTosContent(tosRes.data.content || '');
    } catch (err) { 
        console.error("❌ Lỗi tải dữ liệu Admin:", err.response || err);
        setMessage({ 
            type: 'danger', 
            text: `Lỗi tải dữ liệu: ${err.response?.data?.message || 'Kiểm tra lại quyền truy cập Admin'}` 
        });
    } finally { 
        setLoading(false); 
    }
};

    useEffect(() => { fetchData(); }, []);

    const handleUpdateTos = async () => {
        setSavingTos(true);
        try {
            await api.post('/admin/update-tos', { content: tosContent });
            setMessage({ type: 'success', text: "✅ Đã cập nhật Điều khoản dịch vụ mới!" });
        } catch (err) {
            setMessage({ type: 'danger', text: "❌ Lỗi khi lưu điều khoản." });
        } finally {
            setSavingTos(false);
        }
    };

    const fetchHubInfo = async (accountId) => {
        setSyncingId(accountId);
        try {
            const res = await api.get(`/payment/billing-hub/${accountId}`);
            if (res.data.success) {
                setHubData(prev => ({ ...prev, [accountId]: res.data.data }));
            }
        } catch (err) {
            alert("⚠️ Lỗi kết nối Hub MT5: " + accountId);
        } finally {
            setSyncingId(null);
        }
    };

const handleApprove = async (order) => {
        const isProfitSharing = order.billingType === 'profit_sharing' || order.paymentType === 'profit_sharing';
        const typeText = isProfitSharing ? "THU PHÍ 20% LỢI NHUẬN" : "KÍCH HOẠT GÓI THUÊ";
        const amount = (order.totalPrice || order.amountVnd || 0).toLocaleString();
        
        if (!window.confirm(`XÁC NHẬN DUYỆT: ${typeText}\nMT5: ${order.accountId}\nSố tiền: ${amount} VNĐ\n\nHệ thống sẽ tự động Unblock Bot và gửi Email.`)) return;
        
        setProcessingId(order._id);
        try {
            // SỬA: Dùng .put vì trong Backend bố định nghĩa router.put
            // SỬA: URL phải khớp đúng với route trong routes/order.js
            const res = await api.put(`/api/order/${order._id}/update-status`, {
                status: 'active', // Trạng thái mong muốn khi duyệt
                reason: 'Admin duyệt đơn qua Dashboard'
            }); 
            
            if (res.data.success) {
                setMessage({ 
                    type: 'success', 
                    text: `✅ Đã duyệt thành công MT5 ${order.accountId}. Hệ thống đã gửi Email và kích hoạt Bot!` 
                });
                // Làm mới lại danh sách sau khi duyệt
                fetchData();
            } else {
                throw new Error(res.data.message || "Duyệt đơn thất bại");
            }
        } catch (err) { 
            const errorMsg = err.response?.data?.message || err.message || "Lỗi server khi duyệt đơn";
            console.error("❌ Lỗi duyệt đơn:", err);
            alert("Lỗi: " + errorMsg); 
        } finally {
            setProcessingId(null);
        }
    };

    const handleDeleteOrder = async (orderId, accountId) => {
        if (!window.confirm(`⚠️ CẢNH BÁO: Bạn chắc chắn muốn XÓA vĩnh viễn đơn hàng MT5: ${accountId}?\nHành động này không thể hoàn tác!`)) return;

        try {
            const res = await api.delete(`/admin/delete-order/${orderId}`);
            if (res.data.success) {
                setMessage({ type: 'success', text: `🗑️ Đã xóa đơn hàng ${accountId} khỏi hệ thống!` });
                setOrders(prev => prev.filter(o => o._id !== orderId));
            }
        } catch (err) {
            alert("Lỗi: " + (err.response?.data?.message || "Không thể xóa đơn hàng"));
        }
    };

    const handleRemindIB = async (orderId) => {
        try {
            await api.post(`/admin/remind-ib/${orderId}`);
            setMessage({ type: 'info', text: "📩 Đã gửi yêu cầu xác thực IB tới khách hàng!" });
        } catch (err) { alert("Lỗi gửi yêu cầu"); }
    };

    const handleCreateAgency = async (e) => {
        e.preventDefault();
        try {
            const res = await api.post('/admin/create-agency-quick', agencyForm);
            if (res.data.success) {
                setMessage({ type: 'success', text: `🎉 Nâng cấp đại lý ${agencyForm.agencyName} thành công!` });
                setAgencyForm({ email: '', agencyName: '', agencySlug: '' });
                fetchData();
            }
        } catch (err) { 
            setMessage({ type: 'danger', text: err.response?.data?.message || "Lỗi tạo đại lý" }); 
        }
    };

    return (
        <Container fluid className="py-3 px-lg-4 bg-black min-vh-100 text-white">
            <div className="mb-4 p-4 rounded-4 shadow-lg border-start border-warning border-5" style={{ background: '#0f172a' }}>
                <div className="d-flex justify-content-between align-items-center">
                    <div>
                        <h2 className="text-warning fw-black m-0 uppercase tracking-tighter">NE X BOT - CONTROL PANEL</h2>
                        <p className="text-secondary small m-0 italic">Quản lý dòng tiền & Hệ thống AI Trading</p>
                    </div>
                    <Button variant="outline-warning" onClick={fetchData} disabled={loading} className="rounded-pill px-4 fw-bold shadow-sm">
                        {loading ? <Spinner size="sm" animation="border" className="me-2"/> : <FaSync className="me-2"/>} 
                        LÀM MỚI
                    </Button>
                </div>
            </div>

            {message && <Alert variant={message.type} dismissible className="fw-bold border-0 shadow-sm" onClose={() => setMessage(null)}>{message.text}</Alert>}

            <Nav variant="pills" className="mb-4 bg-dark bg-opacity-50 p-2 rounded-pill shadow-sm border border-secondary border-opacity-25 mx-auto" style={{maxWidth: '650px'}}>
                <Nav.Item className="flex-fill text-center">
                    <Nav.Link active={activeTab === 'orders'} onClick={() => setActiveTab('orders')} className="fw-bold rounded-pill" style={{cursor: 'pointer'}}>
                        <FaClipboardList className="me-2"/> ĐƠN HÀNG {orders.filter(o => ['pending', 'waiting_confirm'].includes(o.status)).length > 0 && <Badge bg="danger" pill className="ms-1">{orders.filter(o => ['pending', 'waiting_confirm'].includes(o.status)).length}</Badge>}
                    </Nav.Link>
                </Nav.Item>
                <Nav.Item className="flex-fill text-center">
                    <Nav.Link active={activeTab === 'agencies'} onClick={() => setActiveTab('agencies')} className="fw-bold rounded-pill" style={{cursor: 'pointer'}}>
                        <FaUserTie className="me-2"/> ĐẠI LÝ
                    </Nav.Link>
                </Nav.Item>
                <Nav.Item className="flex-fill text-center">
                    <Nav.Link active={activeTab === 'tos'} onClick={() => setActiveTab('tos')} className="fw-bold rounded-pill" style={{cursor: 'pointer'}}>
                        <FaFileContract className="me-2"/> ĐIỀU KHOẢN
                    </Nav.Link>
                </Nav.Item>
            </Nav>

            {activeTab === 'orders' && (
                <Card className="bg-dark text-white border-0 shadow-lg rounded-4 overflow-hidden border border-secondary border-opacity-10">
                    <div className="table-responsive">
                        <Table hover variant="dark" className="align-middle text-center mb-0 border-0">
                            <thead className="bg-black text-warning small uppercase">
                                <tr>
                                    <th className="py-3 text-start ps-4">Khách hàng / MT5</th>
                                    <th>Hình Thức</th>
                                    <th>Dữ liệu Hub</th>
                                    <th>IB Check</th>
                                    <th>Số tiền</th>
                                    <th>Trạng Thái</th>
                                    <th>Hành Động</th>
                                </tr>
                            </thead>
                            <tbody>
                                {orders.map(order => {
                                    // 🔥 Sửa logic: Cho phép duyệt cả pending và waiting_confirm
                                    const canApprove = ['pending', 'waiting_confirm'].includes(order.status);
                                    const isDone = ['active', 'completed'].includes(order.status);
                                    const stats = hubData[order.accountId];
                                    const isProfitSharing = order.billingType === 'profit_sharing' || order.paymentType === 'profit_sharing';

                                    return (
                                        <tr key={order._id} className="border-bottom border-secondary border-opacity-10">
                                            <td className="text-start ps-4 py-3">
                                                <div className="fw-bold">{order.user?.email || 'N/A'}</div>
                                                <code className="text-info">ID: {order.accountId}</code>
                                            </td>
                                            <td>
                                                <Badge bg={isProfitSharing ? "warning" : "primary"} text={isProfitSharing ? "dark" : "white"}>
                                                    {isProfitSharing ? "HỢP TÁC 20%" : "THUÊ TRỌN GÓI"}
                                                </Badge>
                                            </td>
                                            <td>
                                                {stats ? (
                                                    <div className="text-start small p-2 bg-black bg-opacity-25 rounded border border-secondary border-opacity-25" style={{minWidth: '120px'}}>
                                                        <div className="text-info">💰 Bal: ${stats.current_balance}</div>
                                                        <div className={stats.current_profit < 0 ? "text-danger" : "text-success"}>
                                                            📈 Pro: ${stats.current_profit}
                                                        </div>
                                                    </div>
                                                ) : (
                                                    <Button variant="link" size="sm" className="text-decoration-none text-secondary" onClick={() => fetchHubInfo(order.accountId)} disabled={syncingId === order.accountId}>
                                                        {syncingId === order.accountId ? <Spinner size="sm" /> : <><FaSync className="me-1"/> Check Hub</>}
                                                    </Button>
                                                )}
                                            </td>
                                            <td>
                                                {order.isExnessReferralConfirmed ? 
                                                    <Badge bg="success" pill className="px-2"><FaCheck/> IB OK</Badge> : 
                                                    <Badge bg="danger" pill className="px-2">CHƯA LINK</Badge>
                                                }
                                            </td>
                                            <td>
                                                <div className={`fw-bold ${isProfitSharing ? 'text-warning' : 'text-white'}`}>
                                                    {(order.totalPrice || order.amountVnd || 0).toLocaleString()}đ
                                                </div>
                                            </td>
                                            <td>
                                                {order.status === 'waiting_confirm' ? (
                                                    <Badge bg="warning" text="dark" className="px-3 py-2 neon-pulse-gold fw-black">⚠️ CHỜ DUYỆT</Badge>
                                                ) : isDone ? (
                                                    <Badge bg="success" className="px-3 py-2 border border-success">✅ HOÀN TẤT</Badge>
                                                ) : (
                                                    <Badge bg="secondary" className="opacity-50">{order.status?.toUpperCase()}</Badge>
                                                )}
                                            </td>
                                            <td>
                                                <div className="d-flex gap-2 justify-content-center align-items-center">
                                                    {canApprove && (
                                                        <Button 
                                                            variant={isProfitSharing ? "warning" : "success"} 
                                                            size="sm" 
                                                            className="fw-bold px-3 shadow-sm"
                                                            onClick={() => handleApprove(order)}
                                                            disabled={processingId === order._id}
                                                        >
                                                            {processingId === order._id ? <Spinner size="sm"/> : (
                                                                isProfitSharing ? <><FaDollarSign/> DUYỆT PHÍ</> : <><FaShieldAlt/> DUYỆT ĐƠN</>
                                                            )}
                                                        </Button>
                                                    )}
                                                    
                                                    {!isDone && (
                                                        <>
                                                            <Button variant="outline-info" size="sm" title="Gửi nhắc nhở IB" onClick={() => handleRemindIB(order._id)}>
                                                                <FaEnvelope />
                                                            </Button>
                                                            <Button variant="outline-danger" size="sm" title="Xóa đơn hàng" onClick={() => handleDeleteOrder(order._id, order.accountId)}>
                                                                <FaTrash />
                                                            </Button>
                                                        </>
                                                    )}
                                                    
                                                    {isDone && (
                                                        <FaCheckCircle className="text-success fs-4" />
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </Table>
                    </div>
                </Card>
            )}

            {/* Các Tab Agencies và TOS giữ nguyên logic của bố nhưng bọc trong Container chuẩn */}
            {activeTab === 'agencies' && (
                <Row className="g-4">
                    <Col lg={4}>
                        <Card className="bg-dark text-white border-0 p-4 rounded-4 border-top border-warning border-3 shadow">
                            <h5 className="text-warning mb-4 fw-black">KÍCH HOẠT ĐẠI LÝ MỚI</h5>
                            <Form onSubmit={handleCreateAgency}>
                                <Form.Group className="mb-3">
                                    <Form.Label className="small text-secondary">Email tài khoản</Form.Label>
                                    <Form.Control className="bg-black border-secondary text-white" type="email" placeholder="user@gmail.com" value={agencyForm.email} onChange={e => setAgencyForm({...agencyForm, email: e.target.value})} required />
                                </Form.Group>
                                <Form.Group className="mb-3">
                                    <Form.Label className="small text-secondary">Tên thương hiệu (Brand)</Form.Label>
                                    <Form.Control className="bg-black border-secondary text-white" type="text" placeholder="NeXBot Global" value={agencyForm.agencyName} onChange={e => setAgencyForm({...agencyForm, agencyName: e.target.value})} required />
                                </Form.Group>
                                <Form.Group className="mb-3">
                                    <Form.Label className="small text-secondary">Mã định danh (Slug)</Form.Label>
                                    <Form.Control className="bg-black border-secondary text-white" type="text" placeholder="nex-global" value={agencyForm.agencySlug} onChange={e => setAgencyForm({...agencyForm, agencySlug: e.target.value})} required />
                                </Form.Group>
                                <Button variant="warning" type="submit" className="w-100 fw-black py-2 mt-2">NÂNG CẤP ĐẠI LÝ</Button>
                            </Form>
                        </Card>
                    </Col>
                    <Col lg={8}>
                        <Card className="bg-dark border-0 rounded-4 overflow-hidden border border-secondary border-opacity-10 shadow">
                            <Table hover variant="dark" className="align-middle text-center mb-0">
                                <thead className="bg-black text-secondary small uppercase">
                                    <tr>
                                        <th className="py-3">Tên Đại lý</th>
                                        <th>Mã Slug</th>
                                        <th>Số dư Ví</th>
                                        <th>Hành động</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {agencies.map(ag => (
                                        <tr key={ag._id}>
                                            <td className="text-start ps-4 py-3">
                                                <div className="fw-bold">{ag.agencyName}</div>
                                                <small className="text-secondary">{ag.user?.email}</small>
                                            </td>
                                            <td><Badge bg="secondary" className="px-3">{ag.agencySlug}</Badge></td>
                                            <td className="text-success fw-bold">{(ag.wallet?.balance || 0).toLocaleString()}đ</td>
                                            <td><Button variant="outline-light" size="sm" className="rounded-pill px-3">CHI TIẾT</Button></td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                        </Card>
                    </Col>
                </Row>
            )}

            {activeTab === 'tos' && (
                <Card className="bg-dark text-white border-0 p-4 rounded-4 border-top border-info border-3 shadow">
                    <div className="d-flex justify-content-between align-items-center mb-4">
                        <h5 className="text-info m-0 fw-black">CẬP NHẬT ĐIỀU KHOẢN DỊCH VỤ</h5>
                        <Button variant="info" className="fw-bold text-dark px-4" onClick={handleUpdateTos} disabled={savingTos}>
                            {savingTos ? <Spinner size="sm" /> : <><FaSave className="me-2"/> LƯU THAY ĐỔI</>}
                        </Button>
                    </div>
                    <Form.Group>
                        <Form.Label className="text-secondary small">Nội dung hiển thị cho khách hàng (Hỗ trợ HTML/Text)</Form.Label>
                        <Form.Control 
                            as="textarea" 
                            rows={15} 
                            className="bg-black border-secondary text-white font-monospace"
                            placeholder="Nhập nội dung điều khoản tại đây..."
                            value={tosContent}
                            onChange={(e) => setTosContent(e.target.value)}
                        />
                    </Form.Group>
                </Card>
            )}

            <style>{`
                .fw-black { font-weight: 900; }
                .neon-pulse-gold { animation: pulse-gold 2s infinite ease-in-out; border: 1px solid rgba(255,255,255,0.2); }
                @keyframes pulse-gold {
                    0% { box-shadow: 0 0 0 0px rgba(234, 179, 8, 0.5); }
                    70% { box-shadow: 0 0 0 10px rgba(234, 179, 8, 0); }
                    100% { box-shadow: 0 0 0 0px rgba(234, 179, 8, 0); }
                }
                .nav-pills .nav-link.active { background-color: #f59e0b !important; color: #000 !important; }
                .nav-pills .nav-link { color: #94a3b8; transition: all 0.3s; }
            `}</style>
        </Container>
    );
};

export default AdminDashboard;